import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { FinalDrivesProvider } from "@/components/FinalDrivesInterstitial";
import { GeoGate } from "@/components/GeoGate";
import i18n from "@/i18n";
import { URL_LANGS, SEG, localizePath, delocalizePath, CUR_LANG } from "@/lib/urls";import Home from "@/pages/Home";
const Brands = lazy(() => import("@/pages/Brands"));
const BrandDetail = lazy(() => import("@/pages/BrandDetail"));
const ModelDetail = lazy(() => import("@/pages/ModelDetail"));
const PartDetail = lazy(() => import("@/pages/PartDetail"));
const SearchPage = lazy(() => import("@/pages/SearchPage"));
const Company = lazy(() => import("@/pages/Company"));
const PartTypePage = lazy(() => import("@/pages/PartTypePage"));
const PartTypeBrandPage = lazy(() => import("@/pages/PartTypeBrandPage"));
const GuidesHub = lazy(() => import("@/pages/GuidesHub"));
const RubberTrackSizes = lazy(() => import("@/pages/RubberTrackSizes"));
const RubberTrackSizeDetail = lazy(() => import("@/pages/RubberTrackSizeDetail"));
const GuidePage = lazy(() => import("@/pages/GuidePage"));
const WhyVanrock = lazy(() => import("@/pages/WhyVanrock"));
const RequestQuote = lazy(() => import("@/pages/RequestQuote"));
const Answers = lazy(() => import("@/pages/Answers"));
const AnswerDetail = lazy(() => import("@/pages/AnswerDetail"));
const BecomeDealer = lazy(() => import("@/pages/BecomeDealer"));
const FindDealer = lazy(() => import("@/pages/FindDealer"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const MissionVisionValues = lazy(() => import("@/pages/MissionVisionValues"));
const Applications = lazy(() => import("@/pages/Applications"));
const CookiePolicy = lazy(() => import("@/pages/CookiePolicy"));
const Complaints = lazy(() => import("@/pages/Complaints"));
const OurPromises = lazy(() => import("@/pages/OurPromises"));
const Sustainability = lazy(() => import("@/pages/Sustainability"));
const Careers = lazy(() => import("@/pages/Careers"));
const Newsroom = lazy(() => import("@/pages/Newsroom"));
const Contact = lazy(() => import("@/pages/Contact"));
const DealerLogin = lazy(() => import("@/pages/DealerLogin"));
const DealerPortal = lazy(() => import("@/pages/DealerPortal"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
import { ChatWidget } from "@/components/ChatWidget";
import { CookieConsent } from "@/components/CookieConsent";
import { RegionProvider } from "@/context/RegionContext";
import { QuoteCartProvider } from "@/context/QuoteCartContext";
import { QuoteCartDrawer } from "@/components/QuoteCartDrawer";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const LegacyRedirect = () => {
  const location = useLocation();
  const cur = location.pathname + location.search;
  const target = localizePath(delocalizePath(cur, CUR_LANG), CUR_LANG);
  if (target !== cur && target !== location.pathname) return <Navigate to={target} replace />;
  return <Navigate to="/" replace />;
};

const AppRoutes = ({ lang }) => {
  const s = (k) => SEG[lang]?.[k] || k;
  return (
    <Suspense fallback={<div className="min-h-screen bg-white" />}>
    <Routes>
      <Route path="" element={<Home />} />
      <Route path={s("brands")} element={<Brands />} />
      <Route path={`${s("brand")}/:brandSlug`} element={<BrandDetail />} />
      <Route path={`${s("brand")}/:brandSlug/:modelSlug`} element={<ModelDetail />} />
      <Route path={`${s("part")}/:partSlug`} element={<PartDetail />} />
      <Route path={s("search")} element={<SearchPage />} />
      <Route path={s("parts")} element={<Navigate to={`/${lang}`} replace />} />
      <Route path={`${s("parts")}/:typeSlug`} element={<PartTypePage />} />
      <Route path={`${s("parts")}/:typeSlug/:brandSlug`} element={<PartTypeBrandPage />} />
      <Route path={s("guides")} element={<GuidesHub />} />
      <Route path={s("rubber-track-sizes")} element={<RubberTrackSizes />} />
      <Route path={`${s("rubber-track-sizes")}/:size`} element={<RubberTrackSizeDetail />} />
      <Route path={`${s("guides")}/:guideSlug`} element={<GuidePage />} />
      <Route path={s("why-vanrock")} element={<WhyVanrock />} />
      <Route path={s("request-quote")} element={<RequestQuote />} />
      <Route path={s("answers")} element={<Answers />} />
      <Route path={`${s("answers")}/:qaSlug`} element={<AnswerDetail />} />
      <Route path={s("company")} element={<Company />} />
      <Route path={s("mission-vision-values")} element={<MissionVisionValues />} />
      <Route path={s("applications")} element={<Applications />} />
      <Route path={s("become-a-dealer")} element={<BecomeDealer />} />
      <Route path={s("dealers")} element={<FindDealer />} />
      <Route path={s("privacy-policy")} element={<PrivacyPolicy />} />
      <Route path={s("cookie-policy")} element={<CookiePolicy />} />
      <Route path={s("complaints")} element={<Complaints />} />
      <Route path={s("our-promises")} element={<OurPromises />} />
      <Route path={s("sustainability")} element={<Sustainability />} />
      <Route path={s("careers")} element={<Careers />} />
      <Route path={s("newsroom")} element={<Newsroom />} />
      <Route path={s("contact")} element={<Contact />} />
      <Route path={s("dealer-login")} element={<DealerLogin />} />
      <Route path={s("dealer-portal")} element={<DealerPortal />} />
      <Route path="admin/login" element={<AdminLogin />} />
      <Route path="admin" element={<AdminDashboard />} />
      <Route path="*" element={<LegacyRedirect />} />
    </Routes>
    </Suspense>
  );
};

const DEAD_LANGS = ["ro", "pl", "tr", "ar", "zh", "ja", "cs", "sv", "id", "hu"];
const _p = window.location.pathname;
const _pair = _p.match(/^\/([a-z]{2})\/([a-z]{2})(?=\/|$)/);
let _redirect = null;
if (_pair && !URL_LANGS.includes(_pair[2])) {
  const rest = _p.slice(_pair[0].length);
  _redirect = (URL_LANGS.includes(_pair[1]) ? `/${_pair[1]}` : "") + rest || "/";
} else {
  const _lm = _p.match(/^\/([a-z]{2})(?=\/|$)/);
  if (_lm && DEAD_LANGS.includes(_lm[1])) _redirect = _p.slice(_lm[0].length) || "/";
}
if (_redirect && _redirect !== _p) window.location.replace(_redirect + window.location.search);
const _m = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/);
const URL_LANG = _m && URL_LANGS.includes(_m[1]) ? _m[1] : null;
const BASENAME = URL_LANG ? `/${URL_LANG}` : "/";

const RootGate = ({ urlLang }) => {
  useEffect(() => {
    if (urlLang) {
      if (i18n.language !== urlLang) i18n.changeLanguage(urlLang);
      return;
    }
    const { pathname, search } = window.location;
    if (pathname.startsWith("/admin")) {
      if (i18n.language !== "en") i18n.changeLanguage("en");
      return;
    }
    const lang = "en";
    const local = localizePath(pathname, lang);
    window.location.replace(`/${lang}${local === "/" ? "" : local}${search}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!urlLang && !window.location.pathname.startsWith("/admin")) {
    return <div className="min-h-screen bg-white" />;
  }
  return <AppRoutes lang={urlLang || "en"} />;
};

function App() {
  return (
    <HelmetProvider>
      <GeoGate>
      <AuthProvider>
        <RegionProvider>
        <FinalDrivesProvider>
        <QuoteCartProvider>
        <div className="App">
          <BrowserRouter basename={BASENAME}>
            <ScrollToTop />
            <Routes>
              <Route path="/*" element={<RootGate urlLang={URL_LANG} />} />
            </Routes>
            <ChatWidget />
            <CookieConsent />
            <QuoteCartDrawer />
          </BrowserRouter>
          <Toaster position="top-right" toastOptions={{ style: { background: "#fff", border: "1px solid #111", color: "#111", borderRadius: 4 } }} />
        </div>
        </QuoteCartProvider>
        </FinalDrivesProvider>
        </RegionProvider>
      </AuthProvider>
      </GeoGate>
    </HelmetProvider>
  );
}

export default App;
